import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, XCircle, Award, RotateCcw, FileText, ChevronRight, Home } from 'lucide-react';
import { typeNames } from '../constants/questionTypes';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface Question {
  id: string;
  type: string;
  title: string;
  options: { id: string; text: string; isCorrect?: boolean }[];
  score: number;
}

interface ExamResultData {
  paperId: string;
  paperTitle: string;
  score: number;
  totalScore: number;
  timeLeft: number;
  examDuration: number;
  answers: Record<string, string>;
  questions: Question[];
}

export default function ExamResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { paperId: urlPaperId } = useParams<{ paperId: string }>();
  const resultData = location.state as ExamResultData | null;

  const [loading, setLoading] = useState(!resultData);
  const [paperId, setPaperId] = useState(resultData?.paperId || urlPaperId || '');
  const [paperTitle, setPaperTitle] = useState(resultData?.paperTitle || '');
  const [score, setScore] = useState(resultData?.score || 0);
  const [totalScore, setTotalScore] = useState(resultData?.totalScore || 0);
  const [timeLeft, setTimeLeft] = useState(resultData?.timeLeft || 0);
  const [examDuration, setExamDuration] = useState(resultData?.examDuration || 3600);
  const [answers, setAnswers] = useState<Record<string, string>>(resultData?.answers || {});
  const [questions, setQuestions] = useState<Question[]>(resultData?.questions || []);
  const [loadError, setLoadError] = useState(false);

  // 分项得分数据
  const [objectiveScore, setObjectiveScore] = useState<number | null>(null);
  const [objectiveTotalScore, setObjectiveTotalScore] = useState<number | null>(null);
  const [programmingScore, setProgrammingScore] = useState<number | null>(null);
  const [gradingStatus, setGradingStatus] = useState<string | null>(null);
  const [hasProgrammingQuestions, setHasProgrammingQuestions] = useState(false);

  // 从数据库加载分项得分数据
  useEffect(() => {
    const currentPaperId = resultData?.paperId || urlPaperId;
    if (!currentPaperId) return;

    const loadScoreData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ action: 'get_records' }),
        });

        const result = await response.json();

        if (result.success && result.records && result.records.length > 0) {
          const currentRecord = result.records.find((r: any) => r.paper_id === currentPaperId);
          if (currentRecord) {
            setObjectiveScore((currentRecord.objectiveScore ?? currentRecord.objective_score) ?? null);
            setObjectiveTotalScore((currentRecord.objectiveTotalScore ?? currentRecord.objective_total_score) ?? null);
            setProgrammingScore((currentRecord.programmingScore ?? currentRecord.programming_score) ?? null);
            setGradingStatus((currentRecord.gradingStatus ?? currentRecord.grading_status) || null);
            const programmingTypes = ['scratch_coding', 'python_coding', 'cpp_coding'];
            const hasProgramming = (currentRecord.questions || []).some((q: any) =>
              programmingTypes.includes(q.type)
            );
            setHasProgrammingQuestions(hasProgramming);
          }
        }
      } catch (error) {
        console.error('加载分项得分数据失败:', error);
      }
    };

    loadScoreData();
  }, [resultData, urlPaperId]);

  // 如果没有 location.state 数据，从数据库重新获取全部数据
  useEffect(() => {
    if (resultData) return; // 有 state 数据，基础数据已从 state 初始化
    if (!urlPaperId) { setLoading(false); setLoadError(true); return; }

    const loadResultFromDb = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) { navigate('/login'); return; }

        // 从 Edge Function 获取答题记录
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ action: 'get_records' }),
        });

        const result = await response.json();

        if (result.success && result.records && result.records.length > 0) {
          const currentRecord = result.records.find((r: any) => r.paper_id === urlPaperId);
          if (currentRecord) {
            setPaperId(currentRecord.paper_id);
            setPaperTitle(currentRecord.paper_title);
            setScore(currentRecord.score);
            setTotalScore(currentRecord.total_score);
            setTimeLeft(currentRecord.time_left);
            setExamDuration(currentRecord.exam_duration || 3600);
            setAnswers(currentRecord.answers || {});
            setQuestions(currentRecord.questions || []);
            setLoading(false);
            return;
          }
        }

        // 没有找到记录，尝试从 exam_progress 获取
        const examKey = `exam_${urlPaperId}`;
        const { data: progress } = await supabase
          .from('exam_progress')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('exam_id', examKey)
          .maybeSingle();

        if (progress && progress.is_submitted) {
          setPaperId(urlPaperId);
          // 获取试卷标题
          const { data: paperData } = await supabase
            .from('papers')
            .select('title, total_score')
            .eq('id', urlPaperId)
            .single();
          setPaperTitle(paperData?.title || '测评结果');
          setScore(progress.score || 0);
          setTotalScore(paperData?.total_score || progress.score || 0);
          setTimeLeft(progress.time_left || 0);
          setExamDuration(3600);
          setAnswers((progress.answers as Record<string, string>) || {});
          // 获取题目列表
          const { data: questionsData } = await supabase
            .from('questions')
            .select('*')
            .eq('paper_id', urlPaperId)
            .order('order_index', { ascending: true });
          if (questionsData) {
            setQuestions(questionsData.map((q: any) => ({
              id: q.id,
              type: q.question_type || 'single_choice',
              title: q.content,
              options: q.options || [],
              score: q.score || 2,
            })));
          }
          setLoading(false);
          return;
        }

        // 都没找到记录
        setLoadError(true);
        setLoading(false);
      } catch (error) {
        setLoadError(true);
        setLoading(false);
      }
    };

    loadResultFromDb();
  }, [resultData, urlPaperId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (loadError || !paperId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">暂无测评结果数据</p>
          <button
            onClick={() => navigate('/exam-platform')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回题库
          </button>
        </div>
      </div>
    );
  }

  // 计算用时
  const usedTime = examDuration - timeLeft;
  const usedHours = Math.floor(usedTime / 3600);
  const usedMinutes = Math.floor((usedTime % 3600) / 60);
  const usedSeconds = usedTime % 60;
  const timeText = usedHours > 0
    ? `${usedHours}小时${usedMinutes}分${usedSeconds}秒`
    : `${usedMinutes}分${usedSeconds}秒`;

  // 客观题类型
  const objectiveTypes = ['single_choice', 'multi_choice', 'judge'];
  const programmingTypes = ['scratch_coding', 'python_coding', 'cpp_coding'];

  // 客观题正确率：优先使用数据库中已保存的 objective_score 和 objective_total_score
  const objectiveQuestions = questions.filter(q => objectiveTypes.includes(q.type));
  const objectiveTotalFromQuestions = objectiveQuestions.reduce((sum, q) => sum + q.score, 0);

  // 使用数据库值计算正确率（一次计算，多处调用）
  const objScore = objectiveScore ?? 0;
  const objTotal = objectiveTotalScore ?? objectiveTotalFromQuestions;
  const accuracy = objTotal > 0 ? Math.round((objScore / objTotal) * 100) : 0;

  // 统计答题情况（仅用于展示答题数量）
  const answeredCount = Object.keys(answers).filter(key => !key.includes('_')).length;
  const unansweredCount = questions.length - answeredCount;
  // 客观题答对/答错数量（仅用于 UI 展示统计卡片）
  const correctCount = objectiveQuestions.filter(q => {
    const userAnswer = answers[q.id];
    if (!userAnswer) return false;
    const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
    if (q.type === 'multi_choice') {
      const userIds = userAnswer.split(',');
      return userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
    }
    return correctIds.includes(userAnswer);
  }).length;
  const wrongCount = answeredCount - correctCount;

  // 按题型分组统计
  const typeStats = questions.reduce((acc, q) => {
    const typeKey = q.type;
    if (!acc[typeKey]) {
      acc[typeKey] = { total: 0, correct: 0, score: 0, totalScore: 0 };
    }
    acc[typeKey].total++;
    acc[typeKey].totalScore += q.score;

    const userAnswer = answers[q.id];
    if (userAnswer) {
      const correctIds = q.options?.filter(o => o.isCorrect).map(o => o.id) || [];
      let isCorrect = false;
      if (q.type === 'multi_choice') {
        const userIds = userAnswer.split(',');
        isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
      } else {
        isCorrect = correctIds.includes(userAnswer);
      }
      if (isCorrect) {
        acc[typeKey].correct++;
        acc[typeKey].score += q.score;
      }
    }
    return acc;
  }, {} as Record<string, { total: number; correct: number; score: number; totalScore: number }>);

  const handleViewDetails = () => {
    navigate(`/quiz/${paperId}`, {
      state: { fromResult: true }
    });
  };

  const handleRestart = () => {
    if (confirm('确定要重新测试吗？')) {
      navigate(`/exam/${paperId}`, { state: { restart: true } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 顶部标题 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 relative"
        >
          <button
            onClick={() => navigate('/')}
            className="absolute left-0 top-0 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            首页
          </button>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{paperTitle}</h1>
          <p className="text-gray-500">本次测评结果</p>
        </motion.div>

        {/* 分项得分卡片 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white mb-6 shadow-lg"
        >
          <div className="grid grid-cols-3 gap-4">
            {/* 客观题得分 */}
            <div className="text-center">
              <div className="text-sm opacity-90 mb-2">客观题得分</div>
              <div className="text-3xl font-bold">
                {objectiveScore ?? 0}分
              </div>
            </div>
            {/* 编程题得分 */}
            <div className="text-center">
              <div className="text-sm opacity-90 mb-2">编程题得分</div>
              <div className="text-3xl font-bold">
                {hasProgrammingQuestions ? (
                  gradingStatus && (gradingStatus === 'final' || gradingStatus === 'teacher_graded' || gradingStatus === 'ai_graded') ? (
                    <span>{programmingScore ?? 0}分</span>
                  ) : (
                    <span className="text-2xl">待阅卷</span>
                  )
                ) : (
                  <span className="text-lg opacity-80">-</span>
                )}
              </div>
            </div>
            {/* 总分 */}
            <div className="text-center">
              <div className="text-sm opacity-90 mb-2">总分</div>
              <div className="text-3xl font-bold">
                {hasProgrammingQuestions && (!gradingStatus || (gradingStatus !== 'final' && gradingStatus !== 'teacher_graded' && gradingStatus !== 'ai_graded')) ? (
                  <span>-</span>
                ) : (
                  <span>{score}分</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm opacity-90 mt-6 pt-4 border-t border-white/20">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>用时：{timeText}</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              <span>客观题正确率：{accuracy}%</span>
            </div>
          </div>
        </motion.div>

        {/* 统计概览 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-4 gap-4 mb-6"
        >
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-1">{answeredCount}</div>
            <div className="text-sm text-gray-500">已答题</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-400 mb-1">{unansweredCount}</div>
            <div className="text-sm text-gray-500">未答题</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-green-500 mb-1">{correctCount}</div>
            <div className="text-sm text-gray-500">答对</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-red-500 mb-1">{wrongCount}</div>
            <div className="text-sm text-gray-500">答错</div>
          </div>
        </motion.div>

        {/* 各题型得分 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            各题型得分
          </h3>
          <div className="space-y-3">
            {Object.entries(typeStats).map(([type, stats], index) => {
              const isProgrammingType = programmingTypes.includes(type);
              const isGraded = gradingStatus === 'final' || gradingStatus === 'teacher_graded' || gradingStatus === 'ai_graded';

              return (
                <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <span className="font-medium text-gray-700">{typeNames[type] || type}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">
                      {isProgrammingType ? `-/${stats.total} 题` : `${stats.correct}/${stats.total} 题`}
                    </span>
                    <span className={`font-bold ${isProgrammingType && !isGraded ? 'text-amber-600' : 'text-blue-600'}`}>
                      {isProgrammingType ? (
                        isGraded ? `${stats.score}/${stats.totalScore} 分` : '待阅卷'
                      ) : (
                        `${stats.score}/${stats.totalScore} 分`
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

        </motion.div>

        {/* 操作按钮 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex gap-4"
        >
          <button
            onClick={handleViewDetails}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-md"
          >
            <FileText className="w-5 h-5" />
            答题详情
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={handleRestart}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-white text-gray-700 border border-gray-200 rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
            重新测试
          </button>
        </motion.div>
      </div>

    </div>
  );
}